/**
 * The answer card (ui/DESIGN.md §3.1): B's kicker line ("RECORD · SALES ORDER · WEB → ERP → WMS → CAR → FIN ·
 * LAST SEEN 3 MIN AGO"), the sentence at title size with every hop a link into the map (A's accent chips,
 * failed hops in --fail), the insights as chips with a tone dot, and one quiet ⋯ with the four lenses and
 * "Tell the story". Looked at as of a moment, an accent chip on top says so ("as of Tue 7 Oct 01:08 · back to
 * now") and every age on the card counts from that moment. Before the preview is in, the same layout as
 * shimmering blocks.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import dayjs from 'dayjs';
import type { AnswerPart, Answer, Insight } from './answer.ts';
import { age } from '../lib/freshness.ts';
import { useNow } from '../lib/asof.tsx';
import { systemCode } from '../lib/systems.ts';
import { RunIcon } from '../components/icons.tsx';
import { Chip, IconButton, Kicker, Menu, NounIcon, NounTag, Surface, cx } from '../design/primitives.tsx';
import { type LensId } from './scope.ts';

export const LENS_LABEL: Record<LensId, string> = { records: 'Records', runs: 'Runs', changes: 'Changes', values: 'Values' };

/** A chip as a plain verb phrase: no "of 80", no clock time, no vendor code ("7 invoices failed · token expired"). */
export function chipText(t: string): string {
  return t.replace(/\bof \d+ /, '').replace(/ (?:yesterday|today|\w{3}) \d{1,2}:\d{2}/, '').replace(/ · [^·]*?\b\d{3},\s*/, ' · ');
}

const MAX_CHIPS = 3;

export function LensIcon({ lens }: { lens: LensId }) {
  if (lens === 'records') return <NounIcon kind="record" size={16} className="!text-current" />;
  if (lens === 'runs') return <NounIcon kind="run" size={16} className="!text-current" />;
  if (lens === 'changes') return <NounIcon kind="document" size={16} className="!text-current" />;
  return <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4"><rect x="2" y="2" width="5" height="5" rx="1.2" /><rect x="9" y="2" width="5" height="5" rx="1.2" /><rect x="2" y="9" width="5" height="5" rx="1.2" /><rect x="9" y="9" width="5" height="5" rx="1.2" /></svg>;
}

export type Hop = Exclude<AnswerPart, string | { ago: number }>;

/** Re-renders every `ms`, so "12 min ago" keeps up. */
function useTick(ms: number): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), ms);
    return () => clearInterval(t);
  }, [ms]);
  return tick;
}

export function AnswerCard({ answer, trail, lens, single, problems, asOf, onBackToNow, onHop, onHopHover, onInsight, onStory, className, compact }: {
  /** null until the preview is in. */
  answer: Answer | null;
  /** The moment the stage looks at the data as of (the scrubber's handle), or null for now. */
  asOf: number | null;
  onBackToNow: () => void;
  /** Values on the trail: a hop with one of them is already chosen. */
  trail: string[];
  lens: LensId | null;
  /** A single record is in focus: the story can be told. */
  single: boolean;
  /** What went wrong, quietly. */
  problems: string[];
  onHop: (hop: Hop) => void;
  onHopHover: (hop: Hop | null) => void;
  onInsight: (i: Insight) => void;
  onStory: () => void;
  className?: string;
  /** A lens is open under the map: the card is a header, not the hero (smaller sentence, two lines). */
  compact?: boolean;
}) {
  const now = useNow();
  useTick(30_000);
  const text = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const [allChips, setAllChips] = useState(false);
  // Three lines (four on a tall screen), then "more": measured, so the card never guesses. A failure at the end of
  // the sentence is never clamped away: the middle hops lose their times first ("It reached ERP, the warehouse and
  // the data warehouse; …"), and if that is still too long the whole sentence shows.
  const [fit, setFit] = useState<Fit>('full');
  const hasFailed = !!answer?.parts.some((p) => typeof p !== 'string' && 'failed' in p && p.failed);
  const canShorten = !!answer?.parts.some((p) => typeof p !== 'string' && 'short' in p && !!p.short);
  const width = useRef(0);
  useLayoutEffect(() => {
    setExpanded(false);
    setAllChips(false);
    setFit('full');
  }, [answer]);
  useLayoutEffect(() => {
    const el = text.current;
    if (!el) return;
    const check = () => {
      // A new width starts over from the full sentence
      if (el.clientWidth !== width.current) {
        width.current = el.clientWidth;
        if (fit !== 'full') {
          setFit('full');
          return;
        }
      }
      const over = el.scrollHeight > el.clientHeight + 2;
      setOverflows(over);
      if (over && !expanded && hasFailed) {
        if (fit === 'full' && canShorten) setFit('short');
        else if (fit !== 'open') setFit('open');
      }
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [answer, fit, expanded, hasFailed, canShorten]);
  const clamped = !expanded && fit !== 'open';
  const parts = answer ? (fit === 'short' && !expanded ? answer.parts.map(shortened) : answer.parts) : [];
  const toggle = fit === 'open' ? false : overflows || expanded || fit === 'short';

  const chosen = new Set(trail.map((v) => v.toLowerCase()));
  const route = answer?.route.map(systemCode) ?? [];

  return (
    <Surface radius="stage" className={cx('stage-dim stage-answer-card flex flex-col', compact ? 'px-5 pt-3 pb-3 gap-1.5 is-compact' : 'px-6 pt-5 pb-5 gap-3', className)}>
      {asOf !== null && (
        <div className="flex">
          <Chip tone="accent" onClick={onBackToNow} onRemove={onBackToNow} className="mv-rise">
            as of <span className="font-mono tabular-nums">{dayjs(asOf).format('ddd D MMM HH:mm')}</span><span className="text-accent/60"> · </span>back to now
          </Chip>
        </div>
      )}
      <div className="flex items-center gap-2.5 min-h-7 flex-wrap">
        {answer ? (
          <>
            {answer.kind !== 'value' && <NounTag kind="record" />}
            <Kicker className="inline-flex items-center gap-x-2 flex-wrap">
              <span>{answer.kind}</span>
              {route.length > 0 && <Dot />}
              {route.length > 0 && <span className="font-mono tracking-[.04em]">{route.join(' → ')}</span>}
              {!!answer.lastSeen && answer.lastSeen > 0 && (
                <>
                  <Dot />
                  <span>last seen {age(answer.lastSeen, now)} ago</span>
                </>
              )}
            </Kicker>
          </>
        ) : (
          <>
            <span className="h-6 w-20 rounded-full mv-shimmer" />
            <span className="h-3.5 w-72 rounded-full mv-shimmer" style={{ animationDelay: '80ms' }} />
          </>
        )}
        {single && (
          <span className="ml-auto -mr-2 -mt-1 flex items-center gap-0.5">
            <Menu.Root>
              <Menu.Trigger asChild>
                <IconButton label="More">
                  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor"><circle cx="3" cy="8" r="1.5" /><circle cx="8" cy="8" r="1.5" /><circle cx="13" cy="8" r="1.5" /></svg>
                </IconButton>
              </Menu.Trigger>
              <Menu.Content align="end">
                <Menu.Item icon={<RunIcon size={16} />} onSelect={onStory}>Tell the story</Menu.Item>
              </Menu.Content>
            </Menu.Root>
          </span>
        )}
      </div>

      {answer ? (
        <p ref={text} className={cx('stage-answer', clamped && 'clamped')}>
          {parts.map((p, i) => renderPart(p, i, now, chosen, onHop, onHopHover))}
        </p>
      ) : (
        <div className="flex flex-col gap-2.5 py-1">
          <span className="h-6 w-[82%] rounded-chip mv-shimmer" />
          <span className="h-6 w-[58%] rounded-chip mv-shimmer" style={{ animationDelay: '120ms' }} />
        </div>
      )}
      {answer && toggle && (
        <button type="button" onClick={() => setExpanded((x) => !x)}
          className="self-start -mt-1 h-7 px-2.5 rounded-full bg-transparent border-0 cursor-pointer text-small text-n8 hover:text-n10 hover:bg-n3 transition-colors duration-[var(--t-micro)]">
          {expanded ? 'less' : '…more'}
        </button>
      )}

      {answer && answer.insights.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-0.5">
          {(allChips ? answer.insights : answer.insights.slice(0, MAX_CHIPS)).map((ins, i) => (
            <Chip key={`${ins.text}${i}`} tone={ins.tone} onClick={() => onInsight(ins)} className="stage-insight mv-rise" style={{ animationDelay: `${i * 40}ms` }}>
              {chipText(ins.text)}
            </Chip>
          ))}
          {!allChips && answer.insights.length > MAX_CHIPS && (
            <Chip tone="neutral" onClick={() => setAllChips(true)} className="mv-rise">+{answer.insights.length - MAX_CHIPS} more</Chip>
          )}
        </div>
      )}
      {problems.length > 0 && <p className="m-0 text-small text-n7">Some of the data did not answer · {problems.join(' · ')}</p>}
    </Surface>
  );
}

const Dot = () => <span className="text-n5" aria-hidden="true">·</span>;

/** full: every word · short: the middle hops without their times · open: not clamped, so the failure at the end shows. */
type Fit = 'full' | 'short' | 'open';

/** A hop in its short form, when it has one. */
const shortened = (p: AnswerPart): AnswerPart => (typeof p !== 'string' && 'short' in p && p.short ? { ...p, text: p.short } : p);

function renderPart(p: AnswerPart, i: number, now: number, chosen: Set<string>, onHop: (h: Hop) => void, onHover: (h: Hop | null) => void): ReactNode {
  if (typeof p === 'string') return <span key={i}>{p}</span>;
  if ('ago' in p) return <span key={i} className="ago">{age(p.ago, now)} ago</span>;
  const on = p.on || chosen.has(p.text.toLowerCase());
  const linked = !!(p.node || p.flow);
  if (!linked) return <span key={i} className={cx(p.failed && 'text-fail')}>{p.text}</span>;
  return (
    <button key={i} type="button" className={cx('stage-hop', p.failed && 'fail', on && 'on')}
      onClick={() => onHop(p)} onPointerEnter={() => onHover(p)} onPointerLeave={() => onHover(null)} onFocus={() => onHover(p)} onBlur={() => onHover(null)}>
      {p.text}
    </button>
  );
}
